import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Ban, AlertTriangle, Trash2, Check, Loader2, CreditCard, Database, UserX, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

interface UserData {
  id: string;
  email: string;
}

interface UserActionsPanelProps {
  user: UserData;
  onActionComplete: () => void;
}

type DeletionStep = 'idle' | 'checking_stripe' | 'cancelling_subs' | 'deleting_data' | 'completed' | 'error';

interface DeletionStepItemProps {
  step: DeletionStep;
  currentStep: DeletionStep;
  stepOrder: DeletionStep[];
  icon: React.ReactNode;
  label: string;
}

function DeletionStepItem({ step, currentStep, stepOrder, icon, label }: DeletionStepItemProps) {
  const currentIndex = stepOrder.indexOf(currentStep);
  const stepIndex = stepOrder.indexOf(step);
  
  const isCompleted = currentStep !== 'error' && stepIndex < currentIndex;
  const isActive = step === currentStep && currentStep !== 'error' && currentStep !== 'completed';
  const isPending = !isCompleted && !isActive;
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
      isCompleted ? 'bg-green-50 border border-green-200' : 
      isActive ? 'bg-primary/10 border border-primary' : 
      'bg-muted/30 border border-border'
    }`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isCompleted ? 'bg-green-500 text-white' :
        isActive ? 'bg-primary text-primary-foreground' :
        'bg-muted text-muted-foreground'
      }`}>
        {isCompleted ? (
          <Check className="h-4 w-4" />
        ) : isActive ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          icon
        )}
      </div>
      <span className={`font-medium ${
        isCompleted ? 'text-green-700' :
        isActive ? 'text-foreground' :
        'text-muted-foreground'
      }`}>
        {label}
      </span>
    </div>
  );
}

export default function UserActionsPanel({ user, onActionComplete }: UserActionsPanelProps) {
  const { t } = useTranslation();
  const [banReason, setBanReason] = useState("");
  const [banDuration, setBanDuration] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Deletion dialog state
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const [deletionStep, setDeletionStep] = useState<DeletionStep>('idle');
  const [deletionError, setDeletionError] = useState<string | null>(null);

  const stepOrder: DeletionStep[] = ['checking_stripe', 'cancelling_subs', 'deleting_data', 'completed'];

  const handleBan = async () => {
    if (banReason.length < 10) {
      toast({ title: t("admin.users.detail.actionsPanel.ban.toast.reasonTooShort"), variant: "error" });
      return;
    }

    setIsProcessing(true);
    try {
      const { error } = await supabase.functions.invoke('ban-user', {
        body: {
          userId: user.id,
          reason: banReason,
          duration: banDuration ? parseInt(banDuration) : null,
        },
      });

      if (error) throw error;

      toast({ title: t("admin.users.detail.actionsPanel.ban.toast.success"), variant: "success" });
      setBanReason("");
      setBanDuration("");
      onActionComplete();
    } catch (error) {
      console.error('Error banning user:', error);
      toast({ title: t("admin.users.detail.actionsPanel.ban.toast.error"), variant: "error" });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWarning = async () => {
    if (warningMessage.length < 10) {
      toast({ title: t("admin.users.detail.actionsPanel.warning.toast.messageTooShort"), variant: "error" });
      return;
    }

    setIsProcessing(true);
    try {
      // Usa il nuovo sistema unificato create-system-notification con template email
      const { error } = await supabase.functions.invoke('create-system-notification', {
        body: {
          userId: user.id,
          type: 'admin_warning',
          titleIt: t("admin.users.detail.actionsPanel.warning.notificationTitle.it"),
          titleEn: t("admin.users.detail.actionsPanel.warning.notificationTitle.en"),
          titleEs: t("admin.users.detail.actionsPanel.warning.notificationTitle.es"),
          descriptionIt: warningMessage,
          descriptionEn: warningMessage,
          descriptionEs: warningMessage,
          sendEmail: true,
          // Use email template from DB
          emailTemplateKey: 'user_warning',
          emailVariables: {
            warningMessage,
          },
        },
      });

      if (error) throw error;

      toast({ title: t("admin.users.detail.actionsPanel.warning.toast.success"), variant: "success" });
      setWarningMessage("");
      onActionComplete();
    } catch (error) {
      console.error('Error sending warning:', error);
      toast({ title: t("admin.users.detail.actionsPanel.warning.toast.error"), variant: "error" });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== "DELETE") {
      toast({ title: t("admin.users.detail.actionsPanel.delete.toast.typeDelete"), variant: "error" });
      return;
    }

    // Open dialog and start deletion process
    setShowDeletionDialog(true);
    setDeletionError(null);
    setIsProcessing(true);
    
    // Step 1: Checking Stripe
    setDeletionStep('checking_stripe');
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Step 2: Cancelling subscriptions
    setDeletionStep('cancelling_subs');
    await new Promise(resolve => setTimeout(resolve, 600));
    
    // Step 3: Deleting data
    setDeletionStep('deleting_data');

    try {
      const { data, error } = await supabase.functions.invoke('delete-user-admin', {
        body: { targetUserId: user.id },
      });

      // Handle FunctionsHttpError
      if (error) {
        let errorMessage = '';
        try {
          const errorContext = (error as any).context;
          if (errorContext?.body) {
            const parsed = JSON.parse(errorContext.body);
            errorMessage = parsed.error || '';
          }
        } catch {
          // If parsing fails, use generic error
        }
        
        setDeletionStep('error');
        if (errorMessage.toLowerCase().includes('admin')) {
          setDeletionError(t("admin.users.detail.actionsPanel.delete.toast.errorAdminRoleDesc"));
        } else {
          setDeletionError(errorMessage || t("admin.users.detail.actionsPanel.delete.toast.error"));
        }
        setDeleteConfirmation("");
        setIsProcessing(false);
        return;
      }
      
      if (data?.error) {
        setDeletionStep('error');
        if (data.error.toLowerCase().includes('admin')) {
          setDeletionError(t("admin.users.detail.actionsPanel.delete.toast.errorAdminRoleDesc"));
        } else {
          setDeletionError(data.error);
        }
        setDeleteConfirmation("");
        setIsProcessing(false);
        return;
      }

      // Step 4: Completed
      await new Promise(resolve => setTimeout(resolve, 500));
      setDeletionStep('completed');
      setDeleteConfirmation("");
      setIsProcessing(false);
      
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeletionStep('error');
      setDeletionError(t("admin.users.detail.actionsPanel.delete.toast.error"));
      setDeleteConfirmation("");
      setIsProcessing(false);
    }
  };

  const handleCloseDeletionDialog = () => {
    if (deletionStep === 'completed') {
      onActionComplete();
    }
    setShowDeletionDialog(false);
    setDeletionStep('idle');
    setDeletionError(null);
  };

  return (
    <div className="space-y-6">
      {/* Ban User */}
      <div className="border-[3px] border-dark p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Ban className="h-5 w-5 text-destructive" />
          <h3 className="font-heading font-bold text-lg">{t("admin.users.detail.actionsPanel.ban.title")}</h3>
        </div>
        
        <BrutalTextarea
          placeholder={t("admin.users.detail.actionsPanel.ban.reasonPlaceholder")}
          value={banReason}
          onChange={(e) => setBanReason(e.target.value)}
          disabled={isProcessing}
        />
        
        <BrutalInput
          type="number"
          placeholder={t("admin.users.detail.actionsPanel.ban.durationPlaceholder")}
          value={banDuration}
          onChange={(e) => setBanDuration(e.target.value)}
          disabled={isProcessing}
        />
        
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <BrutalButton variant="outline" disabled={isProcessing} className="w-full">
              {t("admin.users.detail.actionsPanel.ban.button")}
            </BrutalButton>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("admin.users.detail.actionsPanel.ban.confirmTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("admin.users.detail.actionsPanel.ban.confirmDescription", { email: user.email })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("admin.users.detail.actionsPanel.ban.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={handleBan}>{t("admin.users.detail.actionsPanel.ban.confirm")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Send Warning */}
      <div className="border-[3px] border-dark p-4 space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-warning" />
          <h3 className="font-heading font-bold text-lg">{t("admin.users.detail.actionsPanel.warning.title")}</h3>
        </div>
        
        <BrutalTextarea
          placeholder={t("admin.users.detail.actionsPanel.warning.placeholder")}
          value={warningMessage}
          onChange={(e) => setWarningMessage(e.target.value)}
          disabled={isProcessing}
        />
        
        <BrutalButton 
          variant="secondary" 
          onClick={handleWarning}
          disabled={isProcessing}
          className="w-full"
        >
          {t("admin.users.detail.actionsPanel.warning.button")}
        </BrutalButton>
      </div>

      {/* Delete Account */}
      <div className="border-[3px] border-destructive p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-destructive" />
          <h3 className="font-heading font-bold text-lg text-destructive">{t("admin.users.detail.actionsPanel.delete.title")}</h3>
        </div>
        
        <p className="text-sm text-muted-foreground">
          {t("admin.users.detail.actionsPanel.delete.warningText")}
        </p>
        
        <BrutalInput
          placeholder={t("admin.users.detail.actionsPanel.delete.confirmPlaceholder")}
          value={deleteConfirmation}
          onChange={(e) => setDeleteConfirmation(e.target.value)}
          disabled={isProcessing}
        />
        
        <BrutalButton 
          variant="outline" 
          onClick={handleDelete}
          disabled={isProcessing || deleteConfirmation !== "DELETE"}
          className="w-full border-destructive text-destructive hover:bg-destructive hover:text-white"
        >
          {t("admin.users.detail.actionsPanel.delete.button")}
        </BrutalButton>
      </div>

      {/* Deletion Progress Dialog */}
      <Dialog open={showDeletionDialog} onOpenChange={(open) => {
        // Only allow closing if completed or error
        if (!open && (deletionStep === 'completed' || deletionStep === 'error')) {
          handleCloseDeletionDialog();
        }
      }}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => {
          if (deletionStep !== 'completed' && deletionStep !== 'error') {
            e.preventDefault();
          }
        }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {deletionStep === 'error' ? (
                <>
                  <XCircle className="h-5 w-5 text-destructive" />
                  {t("admin.users.detail.actionsPanel.delete.dialog.errorTitle")}
                </>
              ) : deletionStep === 'completed' ? (
                <>
                  <Check className="h-5 w-5 text-green-500" />
                  {t("admin.users.detail.actionsPanel.delete.dialog.completedTitle")}
                </>
              ) : (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t("admin.users.detail.actionsPanel.delete.dialog.title")}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <p className="text-sm text-muted-foreground mb-4">
              {t("admin.users.detail.actionsPanel.delete.dialog.description", { email: user.email })}
            </p>
            
            {deletionStep !== 'error' ? (
              <>
                <DeletionStepItem
                  step="checking_stripe"
                  currentStep={deletionStep}
                  stepOrder={stepOrder}
                  icon={<CreditCard className="h-4 w-4" />}
                  label={t("admin.users.detail.actionsPanel.delete.dialog.step1")}
                />
                <DeletionStepItem
                  step="cancelling_subs"
                  currentStep={deletionStep}
                  stepOrder={stepOrder}
                  icon={<CreditCard className="h-4 w-4" />}
                  label={t("admin.users.detail.actionsPanel.delete.dialog.step2")}
                />
                <DeletionStepItem
                  step="deleting_data"
                  currentStep={deletionStep}
                  stepOrder={stepOrder}
                  icon={<Database className="h-4 w-4" />}
                  label={t("admin.users.detail.actionsPanel.delete.dialog.step3")}
                />
                <DeletionStepItem
                  step="completed"
                  currentStep={deletionStep}
                  stepOrder={stepOrder}
                  icon={<UserX className="h-4 w-4" />}
                  label={t("admin.users.detail.actionsPanel.delete.dialog.step4")}
                />
              </>
            ) : (
              <div className="p-4 bg-destructive/10 border border-destructive rounded-lg">
                <p className="text-sm text-destructive font-medium">{deletionError}</p>
              </div>
            )}
          </div>
          
          {(deletionStep === 'completed' || deletionStep === 'error') && (
            <div className="flex justify-end">
              <BrutalButton onClick={handleCloseDeletionDialog}>
                {t("admin.users.detail.actionsPanel.delete.dialog.close")}
              </BrutalButton>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
